import React, { useEffect, useState } from 'react';
import { message, Modal, Form, Input, InputNumber, Select } from 'antd';
import { useProjectStore } from '../../hooks/useProjectStore';
import { formationAPI } from '../../services/api';

// ═══════════════════════════════════════════════
// /formation/layers — 分层数据 CRUD 表
// ═══════════════════════════════════════════════
const TH = {
  bg: '#FFFFFF', text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
  headerBg: '#FFFFFF', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  ok: '#1E8449', warn: '#D4770C',
  fontUI: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

const LITH_LABEL: Record<string, string> = {
  sandstone: '砂岩', shale: '页岩', limestone: '灰岩', mudstone: '泥岩',
  dolomite: '白云岩', conglomerate: '砾岩', coal: '煤层', other: '其他',
};

type Layer = {
  id: number;
  name: string;
  top_depth: number;
  bottom_depth: number;
  lithology: string;
  color_code: string;
  is_target: boolean;
  porosity: number | null;
  permeability: number | null;
  water_saturation: number | null;
  toc: number | null;
  dip_angle: number | null;
  dip_azimuth: number | null;
  extent_x: number | null;
  extent_y: number | null;
  description: string;
};

export default function FormationLayersPage() {
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.id;

  const [layers, setLayers] = useState<Layer[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<Layer | 'new' | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await formationAPI.getLayers(projectId);
      const ls: Layer[] = Array.isArray(data) ? data : (data?.results ?? []);
      setLayers(ls.sort((a, b) => a.top_depth - b.top_depth));
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId]);

  const openEdit = (l: Layer | 'new') => {
    setEdit(l);
    form.resetFields();
    if (l !== 'new') {
      form.setFieldsValue(l);
    } else {
      form.setFieldsValue({
        lithology: 'shale', color_code: '#8B7355', is_target: false,
      });
    }
  };

  const submit = async () => {
    const vals = await form.validateFields();
    if (!projectId) return;
    try {
      if (edit && edit !== 'new') {
        // batch_import 全量覆盖 (避免单点编辑接口缺失)
        const next = layers.map((l) => (l.id === edit.id ? { ...l, ...vals } : l));
        await formationAPI.batchImport({ project_id: projectId, layers: next });
        message.success('已更新');
      } else {
        const next = [...layers, vals];
        await formationAPI.batchImport({ project_id: projectId, layers: next });
        message.success('已新增');
      }
      setEdit(null);
      form.resetFields();
      await load();
    } catch (e: any) {
      const d = e?.response?.data;
      message.error(typeof d === 'string' ? d : d?.detail || '保存失败');
    }
  };

  const remove = (l: Layer) => {
    Modal.confirm({
      title: `删除分层 "${l.name}"?`,
      onOk: async () => {
        if (!projectId) return;
        const next = layers.filter((x) => x.id !== l.id);
        await formationAPI.batchImport({ project_id: projectId, layers: next });
        message.success('已删除');
        await load();
      },
    });
  };

  if (!projectId) return <div style={{ padding: 40, color: TH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${TH.headerBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: TH.text, margin: 0 }}>分层数据</h2>
          <span style={{ fontSize: 12, color: TH.textMuted }}>共 {layers.length} 层 {layers.find((l) => l.is_target) && `· 目标层 ${layers.find((l) => l.is_target)?.name}`}</span>
        </div>
        <button onClick={() => openEdit('new')} style={{
          padding: '5px 14px', borderRadius: 4, fontSize: 12, fontWeight: 600,
          background: TH.accent, color: '#fff', border: 'none', cursor: 'pointer',
        }}>+ 新增分层</button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '8px 20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: TH.panelHeaderBg, position: 'sticky', top: 0 }}>
              {['层名', '顶深(m)', '底深(m)', '厚度(m)', '岩性', '颜色', '孔隙度%', '渗透率mD', '含水%', 'TOC%', '倾角°', '倾向°', '目标', '操作'].map((h) => (
                <th key={h} style={{ padding: '6px 10px', textAlign: 'left', borderBottom: `1px solid ${TH.headerBorder}`, color: TH.textSec, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={14} style={{ padding: 30, textAlign: 'center', color: TH.textMuted }}>加载中…</td></tr>
            ) : layers.length === 0 ? (
              <tr><td colSpan={14} style={{ padding: 30, textAlign: 'center', color: TH.textMuted }}>无分层 — 点右上「+ 新增分层」开始</td></tr>
            ) : layers.map((l) => (
              <tr key={l.id} style={{ borderBottom: '1px solid #F0EDE4', background: l.is_target ? TH.accentLight : 'transparent' }}>
                <td style={{ padding: '5px 10px', fontWeight: l.is_target ? 700 : 400, color: l.is_target ? TH.accent : TH.text }}>
                  {l.is_target && '★ '}{l.name}
                </td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{l.top_depth?.toFixed(1)}</td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{l.bottom_depth?.toFixed(1)}</td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{(l.bottom_depth - l.top_depth).toFixed(1)}</td>
                <td style={{ padding: '5px 10px' }}>{LITH_LABEL[l.lithology] || l.lithology}</td>
                <td style={{ padding: '5px 10px' }}>
                  <span style={{ display: 'inline-block', width: 24, height: 12, background: l.color_code, border: '1px solid #ccc', verticalAlign: 'middle' }} />
                </td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{l.porosity ?? '—'}</td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{l.permeability ?? '—'}</td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{l.water_saturation ?? '—'}</td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{l.toc ?? '—'}</td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{l.dip_angle ?? '—'}</td>
                <td style={{ padding: '5px 10px', fontFamily: TH.fontMono }}>{l.dip_azimuth ?? '—'}</td>
                <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                  {l.is_target ? <span style={{ color: TH.accent, fontWeight: 700 }}>✓</span> : ''}
                </td>
                <td style={{ padding: '5px 10px' }}>
                  <button onClick={() => openEdit(l)} style={btnSm}>编辑</button>
                  <button onClick={() => remove(l)} style={{ ...btnSm, marginLeft: 4, color: TH.warn }}>删</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={edit !== null}
        title={edit && edit !== 'new' ? `编辑 ${edit.name}` : '新增分层'}
        onCancel={() => { setEdit(null); form.resetFields(); }}
        onOk={submit}
        okText="保存" cancelText="取消"
        width={640}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Form.Item name="name" label="层名" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="lithology" label="岩性" rules={[{ required: true }]}>
              <Select options={Object.entries(LITH_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
            </Form.Item>
            <Form.Item name="top_depth" label="顶深(m)" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} step={0.1} />
            </Form.Item>
            <Form.Item name="bottom_depth" label="底深(m)" rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} step={0.1} />
            </Form.Item>
            <Form.Item name="color_code" label="显示色">
              <Input placeholder="#8B7355" />
            </Form.Item>
            <Form.Item name="is_target" label="是否目标层" valuePropName="checked">
              <Select options={[{ value: false, label: '否' }, { value: true, label: '是' }]} />
            </Form.Item>
            <Form.Item name="porosity" label="孔隙度 %"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item>
            <Form.Item name="permeability" label="渗透率 mD"><InputNumber style={{ width: '100%' }} step={0.001} /></Form.Item>
            <Form.Item name="water_saturation" label="含水饱和度 %"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item>
            <Form.Item name="toc" label="TOC %"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item>
            <Form.Item name="dip_angle" label="倾角 °"><InputNumber style={{ width: '100%' }} step={0.5} /></Form.Item>
            <Form.Item name="dip_azimuth" label="倾向 °"><InputNumber style={{ width: '100%' }} step={1} /></Form.Item>
          </div>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

const btnSm: React.CSSProperties = {
  padding: '2px 8px', fontSize: 11, border: `1px solid ${TH.headerBorder}`,
  background: '#fff', borderRadius: 3, cursor: 'pointer', color: TH.textSec,
};
